import { makeStyles, shorthands, tokens, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { FieldDef } from "./sections";

const useStyles = makeStyles({
  wrap: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  scrollHint: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.78rem",
    color: BRAND.muted,
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  scrollHintControls: { display: "inline-flex", alignItems: "center", columnGap: "6px" },
  ghostBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: "#f5eef6", ...shorthands.borderColor(BRAND.purple) },
  },
  tableScroll: {
    overflowX: "auto",
    overflowY: "visible",
    maxWidth: "100%",
  },
  table: {
    width: "max-content",
    minWidth: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.05rem",
    color: BRAND.ink,
  },
  thLabel: {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: BRAND.bg,
    width: "300px",
    minWidth: "300px",
    textAlign: "left",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontWeight: 700,
    fontSize: "0.9rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    ...shorthands.borderRight("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  thYear: {
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontWeight: 700,
    fontSize: "1.05rem",
    textAlign: "right",
    fontFamily: BRAND.fontAccent,
    color: BRAND.ink,
    minWidth: "160px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  rowGesamt: {
    backgroundColor: "rgba(247,168,35,0.06)",
  },
  rowDetail: {
    backgroundColor: BRAND.white,
  },
  labelCell: {
    position: "sticky",
    left: 0,
    zIndex: 3,
    backgroundColor: "inherit",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRight("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    width: "300px",
    minWidth: "300px",
  },
  labelCellGesamt: {
    backgroundColor: "rgba(247,168,35,0.10)",
  },
  labelText: {
    fontWeight: 600,
    color: BRAND.ink,
    fontSize: "1.05rem",
  },
  labelTextDetail: {
    fontWeight: 500,
    color: BRAND.ink,
    fontSize: "1rem",
  },
  labelHint: {
    marginTop: "2px",
    fontSize: "0.88rem",
    fontStyle: "italic",
    color: BRAND.muted,
  },
  indent1: { paddingLeft: "32px" },
  indent2: { paddingLeft: "48px" },
  valueCell: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    minWidth: "160px",
    backgroundColor: "inherit",
  },
  input: {
    boxSizing: "border-box",
    width: "100%",
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.05rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
    outlineWidth: 0,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  inputGesamt: {
    fontWeight: 700,
    backgroundColor: BRAND.white,
  },
  unit: {
    marginLeft: "4px",
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  emptyHint: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontStyle: "italic",
    color: BRAND.muted,
    fontSize: "0.88rem",
  },
});

type Props = {
  years: number[];
  onShiftYears: (delta: number) => void;
  gesamt: FieldDef;
  details: FieldDef[];
  /** Wert holen: getValue(fieldKey, year) → string */
  getValue: (fieldKey: string, year: number) => string;
  /** Wert setzen: setValue(fieldKey, year, value) */
  setValue: (fieldKey: string, year: number, value: string) => void;
};

const placeholderFor = (t: FieldDef["type"]): string => {
  switch (t) {
    case "euro":
      return "0,00";
    case "percent":
      return "0";
    case "number":
      return "0";
    default:
      return "";
  }
};

const unitFor = (t: FieldDef["type"]): string => {
  switch (t) {
    case "euro":
      return "€";
    case "percent":
      return "%";
    default:
      return "";
  }
};

const renderField = (
  styles: ReturnType<typeof useStyles>,
  f: FieldDef,
  isGesamt: boolean,
  years: number[],
  getValue: Props["getValue"],
  setValue: Props["setValue"],
) => {
  const indent = isGesamt ? 0 : f.indent ?? 1;
  const indentClass =
    indent === 2 ? styles.indent2 : indent === 1 ? styles.indent1 : "";
  return (
    <tr
      key={f.key}
      className={isGesamt ? styles.rowGesamt : styles.rowDetail}
    >
      <td
        className={mergeClasses(styles.labelCell, indentClass, isGesamt && styles.labelCellGesamt)}
      >
        <div className={isGesamt ? styles.labelText : styles.labelTextDetail}>
          {f.label}
          {unitFor(f.type) && <span className={styles.unit}>{unitFor(f.type)}</span>}
        </div>
        {f.hint && <div className={styles.labelHint}>{f.hint}</div>}
      </td>
      {years.map((y) => (
        <td key={y} className={styles.valueCell}>
          <input
            className={mergeClasses(styles.input, isGesamt && styles.inputGesamt)}
            type="text"
            inputMode={f.type === "text" ? "text" : "decimal"}
            placeholder={placeholderFor(f.type)}
            value={getValue(f.key, y)}
            onChange={(e) => setValue(f.key, y, e.target.value)}
          />
        </td>
      ))}
    </tr>
  );
};

const YearScrollGrid = ({
  years,
  onShiftYears,
  gesamt,
  details,
  getValue,
  setValue,
}: Props) => {
  const styles = useStyles();

  return (
    <div className={styles.wrap}>
      <div className={styles.scrollHint}>
        <span>
          Erfassen Sie die Werte pro Jahr — horizontal scrollbar für weitere Jahre.
        </span>
        <span className={styles.scrollHintControls}>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => onShiftYears(-1)}
            title="Ältere Jahre einblenden"
          >
            ← älter
          </button>
          <span style={{ color: tokens.colorNeutralForeground3 }}>
            {years[0]}–{years[years.length - 1]}
          </span>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => onShiftYears(1)}
            title="Neuere Jahre einblenden"
          >
            neuer →
          </button>
        </span>
      </div>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: 300 }} />
            {years.map((y) => (
              <col key={y} style={{ width: 170 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className={styles.thLabel}>Kennzahl</th>
              {years.map((y) => (
                <th key={y} className={styles.thYear}>
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderField(styles, gesamt, true, years, getValue, setValue)}
            {details.map((f) => renderField(styles, f, false, years, getValue, setValue))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default YearScrollGrid;
